import { RequireAuth, RequirePinChanged } from '@saniplus/ui';
import { createBrowserRouter, type RouteObject } from 'react-router';

import { AppLayout } from '../components/AppLayout';
import { NO_PORTAL_ACCESS_PATH } from '../components/nav';
import { PendingRoute } from '../components/PendingRoute';
import { RouteError } from '../components/RouteError';
import { pinRoute, publicRoutes } from '../features/auth/routes';

/**
 * Library ("data") mode, code-split per route.
 *
 * The nesting is the authorization story, read top to bottom: a session, then
 * a PIN that is not overdue, then the shell. `/pin` deliberately sits inside
 * `RequireAuth` but outside `RequirePinChanged` — it is the one authenticated
 * screen a user with `mustChangePin` is allowed to reach.
 */

/**
 * Every feature's own route table, collected at build time.
 *
 * A feature owns `src/features/<name>/routes.tsx` and exports a `routes` array
 * from it; this file never imports a feature's *pages*. `import.meta.glob` with
 * `eager: true` costs nothing at runtime — those modules contain only route
 * descriptors whose components are still behind `lazy()` — and it means a
 * feature landing in a parallel branch is wired the moment its file exists,
 * with no edit here and no merge conflict in a file eleven agents touch.
 */
const modules = import.meta.glob<{ readonly routes?: RouteObject[] }>(
  '../features/*/routes.tsx',
  { eager: true },
);

function featureRoutes(): RouteObject[] {
  const collected: RouteObject[] = [];
  for (const [path, module] of Object.entries(modules)) {
    // `auth` is placed by hand: `/login` is public and `/pin` sits between the
    // two guards, so neither belongs in the shell's children.
    if (path.includes('/auth/')) continue;
    for (const route of module.routes ?? []) collected.push(route);
  }
  return collected;
}

/**
 * The routes the navigation offers (FRONTEND-BRIEF §4B), so a nav item never
 * leads to the 404 page while its feature is still in flight.
 *
 * A path already claimed by a feature is dropped from this list — the slot
 * disappears on its own. Anything still here at review time is a screen nobody
 * has built yet, and the list is the checklist.
 */
export const NAV_PATHS: readonly string[] = [
  '/horaires/calendrier',
  '/horaires/series',
  '/horaires/ouverts',
  '/pointages/en-cours',
  '/pointages',
  '/pointages/:id',
  '/taches',
  '/taches/nouvelle',
  '/taches/:id',
  '/heures/feuilles',
  '/heures/exports',
  '/exceptions',
  '/exceptions/:id',
  '/organisation/contrats',
  '/organisation/sites',
  '/personnel/employes',
  '/personnel/employes/:id',
  '/audit',
  '/parametres',
];

/**
 * Every path a route tree actually serves, children resolved against parents.
 *
 * Features nest differently and all of them are correct: `pointages` mounts a
 * gate at `/pointages` with relative children, `horaires` declares five
 * siblings with no leading slash, `organisation` writes absolute paths. Reading
 * only the top-level `path` would leave this file registering a second
 * `/heures/feuilles` beside the real one, and two matching routes is a coin
 * toss, not a fallback.
 */
function servedPaths(routes: readonly RouteObject[], parent = ''): string[] {
  const served: string[] = [];
  for (const route of routes) {
    const own = route.path ?? '';
    const full = own.startsWith('/') ? own : `${parent}/${own}`.replace(/\/+/gu, '/');
    if (route.path !== undefined) served.push(full.replace(/\/$/u, '') || '/');
    if (route.children !== undefined) {
      served.push(...servedPaths(route.children, full.replace(/\/$/u, '')));
    }
  }
  return served;
}

function pendingRoutes(claimed: RouteObject[]): RouteObject[] {
  const taken = new Set(servedPaths(claimed));
  return NAV_PATHS.filter((path) => !taken.has(path)).map((path) => ({
    path,
    Component: PendingRoute,
  }));
}

const FEATURE_ROUTES = featureRoutes();

/**
 * The nav paths no feature serves yet. Empty is the goal; `router.spec.ts`
 * asserts what is still on it, so a slot cannot quietly become permanent.
 */
export const PENDING_PATHS: readonly string[] = pendingRoutes(FEATURE_ROUTES)
  .map((route) => route.path)
  .filter((path): path is string => path !== undefined);

const shellChildren: RouteObject[] = [
  {
    index: true,
    lazy: async () => ({ Component: (await import('./IndexRedirect')).IndexRedirect }),
  },
  ...FEATURE_ROUTES,
  ...pendingRoutes(FEATURE_ROUTES),
  /*
   * The 404 a signed-in supervisor actually meets, INSIDE the shell.
   *
   * The top-level `*` below is still there for a visitor with no session, but
   * it used to catch everybody — so mistyping a URL replaced the whole portal
   * with a bare page carrying one link and no `<main>`. A wrong address is a
   * typo, not a sign-out: the nav, the account menu and the exceptions badge
   * all stay, and the way back is one click.
   */
  {
    path: '*',
    lazy: async () => ({ Component: (await import('../pages/NotFoundPage')).NotFoundPage }),
  },
];

export const router = createBrowserRouter([
  {
    ErrorBoundary: RouteError,
    children: [
      ...publicRoutes,
      {
        Component: RequireAuth,
        children: [
          pinRoute,
          {
            Component: RequirePinChanged,
            children: [
              /*
               * Q6 (Phase 4): an account with a session and no portal
               * permission — a worker signed in here — lands on an explanation
               * instead of an empty shell. Outside `AppLayout` on purpose: the
               * shell with nothing in it IS the problem the screen explains.
               */
              {
                path: NO_PORTAL_ACCESS_PATH,
                lazy: async () => ({
                  Component: (await import('../components/NoPortalAccess')).NoPortalAccess,
                }),
              },
              {
                Component: AppLayout,
                children: shellChildren,
              },
            ],
          },
        ],
      },
      {
        path: '*',
        lazy: async () => ({
          Component: (await import('../pages/NotFoundPage')).StandaloneNotFoundPage,
        }),
      },
    ],
  },
]);
